/**
 * SELECTOR COMPONENT
 *
 * Enhanced select dropdown component built on react-select with additional features:
 *
 * - Multi-select capability for language selection
 * - Custom error styling and validation integration
 * - Defensive programming for data integrity
 * - Advanced filtering with multi-word search support
 * - Accessible design following Bootstrap patterns
 *
 * Used throughout the application for:
 * - Language selection in surveys
 * - School selection
 * - Country selection
 * - Any dropdown with search and multi-select needs
 *
 * Features robust error handling and type checking to prevent runtime errors
 * with malformed data from API endpoints.
 */

import React from 'react'
import Select from 'react-select'

const Selector = ({
  options = [], // Available options for selection
  name, // Form field name for Formik integration
  error, // Error message for validation display
  isSearchable = true, // Enable/disable search functionality
  id, // HTML id for accessibility
  isMulti = false, // Enable multi-select mode
  value, // Current selected value(s)
  onChange, // Change handler function
  filterOption, // Custom filter function
  ...rest // Additional props passed to react-select
}) => {
  /**
   * ERROR HANDLING AND CUSTOM STYLING
   *
   * Applies Bootstrap-style error styling when validation fails.
   * Uses custom CSS to show error indicators and red border styling
   * consistent with other form elements in the application.
   */
  const isError = !!error
  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: isError ? '#dc3545' : provided.borderColor,
      ...(isError && {
        // Add error icon using SVG data URI
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right calc(.5em + 2rem) center',
        backgroundSize: 'calc(.75em + .375rem) calc(.75em + .375rem)',
      }),
      className: isError ? 'is-invalid' : '',
    }),
  }

  /**
   * VALUE SAFETY AND TYPE CHECKING
   *
   * Ensures that the value passed to react-select is always in the correct format.
   * Prevents runtime errors from malformed data by providing safe defaults:
   * - Multi-select: Always an array (empty if invalid)
   * - Single-select: Object or null (never undefined or primitive)
   */
  const safeValue = isMulti
    ? Array.isArray(value)
      ? value
      : []
    : value && typeof value === 'object'
    ? value
    : null

  /**
   * ADVANCED FILTERING FUNCTIONALITY
   *
   * Custom filter function with multi-word search support and robust error handling.
   * Features:
   * - Multi-word search (e.g., "spanish spa" matches "Spanish (spa)")
   * - Case-insensitive matching
   * - Defensive programming against malformed data
   * - Comprehensive error logging for debugging
   *
   * This is particularly useful for language selection where users might
   * search by language name, ISO code, or partial matches.
   */
  const defaultFilterOption = React.useCallback((candidate, input) => {
    // Validate candidate object structure
    if (!candidate || typeof candidate !== 'object') {
      console.error('Selector filterOption: candidate is malformed', candidate)
      return false
    }
    if (!candidate.label) {
      console.error(
        'Selector filterOption: candidate.label is missing',
        candidate
      )
      return false
    }

    // Validate input parameter
    if (typeof input !== 'string') {
      console.error('Selector filterOption: input is not a string', input)
      return false
    }

    const safeInput = input || ''
    if (!safeInput) return true // Show all options if no search input

    // Multi-word search: all words must match somewhere in the label
    const searchWords = safeInput.toLowerCase().split(/\s+/)
    const label = String(candidate.label).toLowerCase()
    return searchWords.some((word) => label.includes(word))
  }, [])

  return (
    <div>
      <Select
        name={name}
        inputId={id || name}
        options={options}
        isSearchable={isSearchable}
        isMulti={isMulti}
        value={safeValue}
        onChange={onChange}
        styles={customStyles}
        aria-invalid={isError}
        aria-describedby={isError ? `${name}-error` : undefined}
        filterOption={filterOption || defaultFilterOption}
        {...rest}
      />
      {isError && (
        <div id={`${name}-error`} className="invalid-feedback d-block">
          {error?.message}
        </div>
      )}
    </div>
  )
}

export default Selector
